import type { Segment } from '@/lib/types'
import type { EvidenceRow } from '@/lib/ensemble/types'

// Deterministic seed — always same demo data
export const DEMO_PROJECT_ID = 'proj_fin_fees_demo'
export const DEMO_PANEL_ID = 'panel_fin_fees_demo'
export const DEMO_TENANT_ID = 'tnt_demo'
export const DEMO_WORKSPACE_ID = 'wks_demo'

export const DEMO_PRE_READ_QUESTION = 'Which fee disclosure message builds the most trust with bank customers?'

export const DEMO_STIMULI: Array<{ stimulus_id: string; label: string; content: string; type: string }> = [
  {
    stimulus_id: 'stim_a',
    label: 'Message A',
    content: 'Effective the first of next month, a monthly maintenance fee will apply to your account. Waiver conditions apply. Please refer to your account agreement for full terms and eligibility requirements.',
    type: 'message',
  },
  {
    stimulus_id: 'stim_b',
    label: 'Message B',
    content: 'Starting next month, we\'re adding a $12 monthly maintenance fee. You can avoid it completely — just keep a minimum daily balance of $500, set up a monthly direct deposit, or make 10 or more debit purchases per month.',
    type: 'message',
  },
  {
    stimulus_id: 'stim_c',
    label: 'Message C',
    content: 'We\'re updating your account to include enhanced features and expanded service options. A small monthly fee helps us continue delivering the banking experience you deserve.',
    type: 'message',
  },
]

export const DEMO_SEGMENTS: Segment[] = [
  {
    segment_id: 'seg_college_educated',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_fin_fees_demo',
    name: 'College-Educated Customers',
    respondent_count: 612,
    demographic_skew: { age_median: 38, education: 'bachelor\'s or higher', income_band: '$60k–$120k' },
    dominant_themes: [
      'Transparency and plain language builds trust',
      'Concrete waiver conditions reduce perceived unfairness',
      'Legal boilerplate signals bank is hiding something',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'Message B actually tells me what I need to do to avoid the fee. That\'s all I wanted to know.', segment: 'College-Educated Customers', source_file: 'fee-disclosure-survey.csv', source_row: 34 },
      { text: '"Waiver conditions apply" means nothing to me without knowing what those conditions are.', segment: 'College-Educated Customers', source_file: 'fee-disclosure-survey.csv', source_row: 89 },
      { text: 'The "enhanced features" message is obviously trying to spin a fee as a benefit. Insulting.', segment: 'College-Educated Customers', source_file: 'fee-disclosure-survey.csv', source_row: 156 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_non_college',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_fin_fees_demo',
    name: 'Non-College-Educated Customers',
    respondent_count: 298,
    demographic_skew: { age_median: 42, education: 'high school or some college', income_band: '$30k–$65k' },
    dominant_themes: [
      'Legal language creates confusion and anxiety',
      'Plain English waiver conditions feel fair and actionable',
      'Ambiguous messages trigger distrust and account closure intent',
    ],
    evidence_strength: 'High',
    representative_quotes: [
      { text: 'I don\'t know what "waiver conditions" means. Is this optional? Do I have to do something? I\'m confused.', segment: 'Non-College-Educated Customers', source_file: 'fee-disclosure-survey.csv', source_row: 412 },
      { text: 'Message B is the only one that told me how much and what I can do about it. The others feel like a trap.', segment: 'Non-College-Educated Customers', source_file: 'fee-disclosure-survey.csv', source_row: 478 },
      { text: 'Banks always hide stuff in fine print. Message A feels exactly like that.', segment: 'Non-College-Educated Customers', source_file: 'fee-disclosure-interviews.txt', source_row: 8 },
    ],
    created_at: 1700000000000,
  },
  {
    segment_id: 'seg_high_balance',
    tenant_id: DEMO_TENANT_ID,
    workspace_id: DEMO_WORKSPACE_ID,
    project_id: DEMO_PROJECT_ID,
    upload_id: 'upload_fin_fees_demo',
    name: 'High-Balance Customers',
    respondent_count: 188,
    demographic_skew: { age_median: 51, avg_balance: '$4,200', income_band: '$100k+' },
    dominant_themes: [
      'Waiver threshold confirmation provides peace of mind',
      'Explicit balance minimum validates existing behavior',
      'Feature-framing messaging feels condescending at this tier',
    ],
    evidence_strength: 'Medium',
    representative_quotes: [
      { text: 'I keep well over $500 in my account. Knowing exactly what the waiver threshold is gives me confidence I\'m covered.', segment: 'High-Balance Customers', source_file: 'fee-disclosure-survey.csv', source_row: 734 },
      { text: 'Message A just makes me anxious without telling me if I qualify for the waiver. Message B removes that uncertainty.', segment: 'High-Balance Customers', source_file: 'fee-disclosure-survey.csv', source_row: 788 },
    ],
    created_at: 1700000000000,
  },
]

export const DEMO_EVIDENCE: EvidenceRow[] = [
  { respondent_id: 'R001', segment: 'College-Educated Customers', response: 'Message B', open_text: 'Message B actually tells me what I need to do to avoid the fee. That\'s all I wanted to know.', source_file: 'fee-disclosure-survey.csv', row_index: 34 },
  { respondent_id: 'R002', segment: 'College-Educated Customers', response: 'Message A', open_text: '"Waiver conditions apply" means nothing to me without knowing what those conditions are.', source_file: 'fee-disclosure-survey.csv', row_index: 89 },
  { respondent_id: 'R003', segment: 'College-Educated Customers', response: 'Message C', open_text: 'The "enhanced features" message is obviously trying to spin a fee as a benefit. Insulting.', source_file: 'fee-disclosure-survey.csv', row_index: 156 },
  { respondent_id: 'R004', segment: 'College-Educated Customers', response: 'Message B', open_text: 'Concrete numbers and three specific paths to avoid the fee — this is what good communication looks like.', source_file: 'fee-disclosure-survey.csv', row_index: 212 },
  { respondent_id: 'R005', segment: 'Non-College-Educated Customers', response: 'Message A', open_text: 'I don\'t know what "waiver conditions" means. Is this optional? Do I have to do something? I\'m confused.', source_file: 'fee-disclosure-survey.csv', row_index: 412 },
  { respondent_id: 'R006', segment: 'Non-College-Educated Customers', response: 'Message B', open_text: 'Message B is the only one that told me how much and what I can do about it. The others feel like a trap.', source_file: 'fee-disclosure-survey.csv', row_index: 478 },
  { respondent_id: 'R007', segment: 'Non-College-Educated Customers', response: 'Message A', open_text: 'Banks always hide stuff in fine print. Message A feels exactly like that.', source_file: 'fee-disclosure-interviews.txt', row_index: 8 },
  { respondent_id: 'R008', segment: 'Non-College-Educated Customers', response: 'Message B', open_text: 'I appreciated that Message B told me the exact dollar amount and three ways to get around it. Very clear.', source_file: 'fee-disclosure-survey.csv', row_index: 531 },
  { respondent_id: 'R009', segment: 'High-Balance Customers', response: 'Message B', open_text: 'I keep well over $500 in my account. Knowing exactly what the waiver threshold is gives me confidence I\'m covered.', source_file: 'fee-disclosure-survey.csv', row_index: 734 },
  { respondent_id: 'R010', segment: 'High-Balance Customers', response: 'Message A', open_text: 'Message A just makes me anxious without telling me if I qualify for the waiver. Message B removes that uncertainty.', source_file: 'fee-disclosure-survey.csv', row_index: 788 },
  { respondent_id: 'R011', segment: 'High-Balance Customers', response: 'Message C', open_text: '"Enhanced features" — I\'ve had this account for fifteen years. Don\'t sell me. Just tell me what changed.', source_file: 'fee-disclosure-survey.csv', row_index: 812 },
  { respondent_id: 'R012', segment: 'College-Educated Customers', response: 'Message B', open_text: 'Any time a bank is clear about what you can do to avoid fees, that\'s a bank worth staying with.', source_file: 'fee-disclosure-interviews.txt', row_index: 22 },
]
